import * as Sentry from '@sentry/vue';

/**
 * Lightweight logger wrapper inspired by QuintoAndar's node-logger
 * (see docs/reference-improvements/11-seguranca-jwt-logging.md §B.5).
 *
 * Goals:
 *  - `debug`/`info` only emit outside of production (no noisy console in prod).
 *  - `warn`/`error` always forward structured context to Sentry, tagging the
 *    originating `module` so logs are searchable.
 *  - Errors become Sentry exceptions; everything else becomes a breadcrumb
 *    (for warn/error a message-level event is also captured), mirroring the
 *    node-logger mapping of levels to Sentry severity.
 *
 * Usage:
 *   import { createLogger } from 'shared/helpers/logger';
 *   const logger = createLogger('conversations/store');
 *   logger.debug('loaded', { count });
 *   logger.error(error, { conversationId });
 */

// Resolve "are we in production" using the project's Vite env, with a safe
// fallback for non-Vite runtimes (tests / SSR-less contexts).
export const isProduction = () => {
  try {
    if (typeof import.meta !== 'undefined' && import.meta.env) {
      return (
        import.meta.env.PROD === true || import.meta.env.MODE === 'production'
      );
    }
  } catch (e) {
    // import.meta not available in this runtime; fall through.
  }

  if (typeof process !== 'undefined' && process.env) {
    return process.env.NODE_ENV === 'production';
  }

  return false;
};

const SENTRY_LEVELS = {
  debug: 'debug',
  info: 'info',
  warn: 'warning',
  error: 'error',
};

const isError = value => value instanceof Error;

const toMessage = value => {
  if (value === undefined || value === null) return '';
  if (typeof value === 'string') return value;
  if (isError(value)) return value.message;
  try {
    return JSON.stringify(value);
  } catch (e) {
    return String(value);
  }
};

const addBreadcrumb = (moduleName, level, message, context) => {
  Sentry.addBreadcrumb({
    category: moduleName,
    level: SENTRY_LEVELS[level] || 'info',
    message,
    data: { module: moduleName, ...context },
  });
};

/**
 * Create a scoped logger for a given module.
 * @param {string} moduleName - identifier for the call site (file/component).
 * @returns {{ debug: Function, info: Function, warn: Function, error: Function }}
 */
export const createLogger = (moduleName = 'app') => {
  const prefix = `[${moduleName}]`;

  // debug/info: dev-only console output, no Sentry event.
  const devLog = (consoleMethod, level) => (message, context) => {
    if (isProduction()) return;
    // eslint-disable-next-line no-console
    console[consoleMethod](prefix, message, context ?? '');
    addBreadcrumb(moduleName, level, toMessage(message), context);
  };

  return {
    debug: devLog('debug', 'debug'),

    info: devLog('info', 'info'),

    // warn: breadcrumb + Sentry message event, dev console mirror.
    warn: (message, context = {}) => {
      if (!isProduction()) {
        // eslint-disable-next-line no-console
        console.warn(prefix, message, context);
      }
      addBreadcrumb(moduleName, 'warn', toMessage(message), context);
      Sentry.captureMessage(`${prefix} ${toMessage(message)}`, {
        level: 'warning',
        tags: { module: moduleName },
        extra: context,
      });
    },

    // error: Sentry exception when given an Error, otherwise a message event.
    error: (errorOrMessage, context = {}) => {
      if (!isProduction()) {
        // eslint-disable-next-line no-console
        console.error(prefix, errorOrMessage, context);
      }
      addBreadcrumb(moduleName, 'error', toMessage(errorOrMessage), context);

      if (isError(errorOrMessage)) {
        Sentry.captureException(errorOrMessage, {
          tags: { module: moduleName },
          extra: context,
        });
      } else {
        Sentry.captureMessage(`${prefix} ${toMessage(errorOrMessage)}`, {
          level: 'error',
          tags: { module: moduleName },
          extra: context,
        });
      }
    },
  };
};

export default createLogger;
